import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import NavHeader from "../shared/components/NavHeader";
import GuestSearchBar from "../shared/components/GuestSearchBar";
import DropdownMenu, { MenuItem } from "../shared/components/DropdownMenu";
import GuestFilterTabs from "./components/GuestFilterTabs";
import GuestListItem from "./components/GuestListItem";
import GuestListStore from "./store/guestListStore";
import EventStore from "../event/store/eventStore";
import CommunityNavigationStore from "../navigation/communityNavigationStore";
import RemoveGuestConfirmation from "./components/GuestListRemovalPopUp";
import { useSelectAttendeesDialog } from "../select-attendees/hooks";
import { sendEventTextBlast } from "../../../../api/sitch_events";

interface GuestListPageProps {
  onBack: () => void;
}

const GuestListPageComponent: React.FC<GuestListPageProps> = ({ onBack }) => {
  const { filterCounts, attendees, filteredGuests } = GuestListStore;

  const { dialog, selectAttendees } = useSelectAttendeesDialog();

  useEffect(() => {
    if (EventStore.sitchEventData?.guid) {
      GuestListStore.loadAttendees(EventStore.sitchEventData.guid);
    }
  }, [EventStore.sitchEventData?.guid]);

  const handleSendReminders = async () => {
    const results = await selectAttendees(attendees);
    if (!results || results.memberIds.length === 0) return;
    await sendEventTextBlast({
      event: EventStore.sitchEventData!.guid!,
      members: results.memberIds,
      message: results.message,
    });
    toast.success("Text blast sent successfully!");
  };

  const handleRemoveGuest = (guestId: string) => {
    CommunityNavigationStore.setPopUpView({
      onClose: () => CommunityNavigationStore.setPopUpView(null),
      children: (
        <RemoveGuestConfirmation
          onConfirm={() => {
            GuestListStore.removeGuest(guestId);
          }}
        />
      ),
    });
  };

  const handleSendInviteLink = () => {
    EventStore.sendInviteLink();
  };

  const menuItems: MenuItem[] = [
    {
      label: "🔔 Send reminders",
      onClick: handleSendReminders,
    },
    {
      label: "🔗 Send invite link",
      onClick: handleSendInviteLink,
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-black text-white">
        <NavHeader onBack={onBack} />

        <div className="max-w-md mx-auto px-6 pt-6 pb-4">
          <div className="flex items-baseline justify-between mb-6 relative">
            <h1 className="text-4xl font-nostalgic">GUEST LIST</h1>
            <DropdownMenu menuItems={menuItems} />
          </div>

          <GuestSearchBar
            value={GuestListStore.searchQuery}
            onChange={GuestListStore.setSearchQuery}
            placeholder="Search guests here..."
          />

          <GuestFilterTabs
            activeFilter={GuestListStore.activeFilter}
            onFilterChange={GuestListStore.setActiveFilter}
            counts={filterCounts}
          />
        </div>

        <div className="max-w-md mx-auto px-6 pb-8">
          {filteredGuests.map((guest) => (
            <GuestListItem
              key={guest.guid}
              guest={guest}
              onRemoveGuestAction={handleRemoveGuest}
            />
          ))}

          {filteredGuests.length === 0 && (
            <div className="text-center py-8">
              <p className="text-white/60">
                {GuestListStore.searchQuery
                  ? "No guests found matching your search."
                  : "No guests added yet"}
              </p>
            </div>
          )}
        </div>
      </div>
      {dialog}
    </>
  );
};

export const GuestListPage = observer(GuestListPageComponent);

export default GuestListPage;
